/**
 * Launch · Checklist Workflow Engine (Wave E).
 *
 * Transforms the flat checklist into a stage-oriented workflow:
 * Planning → R&D → Sampling → Blind → Decision → Batch → QC →
 * Packaging → Marketing → Website → Shopify → Warehouse → Launch → Post.
 *
 * Every item drills into Done / Blocked / Approval / Owner / Due /
 * Comments / Attachments / Timeline via existing primitives.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { CHECKLIST, LAUNCH_PROJECTS, ChecklistItem } from "../../mocks/launchData";
import { api } from "../../services/api";
import { upsertLocal } from "../../services/store";

const STATUS_META: Record<ChecklistItem["status"], { bg: string; fg: string; label: string }> = {
  done:          { bg: "var(--ph-success-tint)", fg: "var(--ph-success)",  label: "Done"          },
  "in-progress": { bg: "var(--ph-ai-tint)",       fg: "var(--ph-ai)",       label: "In-progress"   },
  blocked:       { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",   label: "Blocked"       },
  todo:          { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)", label: "Todo"          },
};

/** Contract-aligned stages. Categories from the mock map to the closest stage. */
const STAGE_MAP: Record<ChecklistItem["category"], string> = {
  Formula:     "R&D",
  Regulatory:  "Decision",
  Packaging:   "Packaging",
  Supply:      "Batch",
  Marketing:   "Marketing",
  Retail:      "Warehouse",
  Digital:     "Website / Shopify",
  Fulfillment: "Warehouse",
};

const STAGES = [
  "Planning", "R&D", "Sampling", "Blind", "Decision", "Batch", "QC",
  "Packaging", "Marketing", "Website / Shopify", "Warehouse", "Launch", "Post",
] as const;

function launchName(id: string) { return LAUNCH_PROJECTS.find((l) => l.id === id)?.productName ?? id; }
function stageForItem(c: ChecklistItem): string {
  return STAGE_MAP[c.category] ?? "Planning";
}

type ChkTab = "overview" | "owner" | "due" | "approval" | "blocked" | "comments" | "attachments" | "timeline" | "audit";

export default function Checklist() {
  const [tab, setTab] = useState<ChecklistItem["status"] | "all">("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<ChecklistItem | null>(null);
  const [wsTab, setWsTab] = useState<ChkTab>("overview");

  const filtered = useMemo(() => CHECKLIST.filter((c) =>
    (tab === "all" || c.status === tab) &&
    (!q || c.title.toLowerCase().includes(q.toLowerCase()) || launchName(c.launchId).toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);

  const byStage = useMemo(() => {
    const map = new Map<string, ChecklistItem[]>();
    filtered.forEach((c) => {
      const s = stageForItem(c);
      map.set(s, [...(map.get(s) || []), c]);
    });
    return map;
  }, [filtered]);

  async function transition(status: ChecklistItem["status"]) {
    if (!selected) return;
    try {
      const row = await api.patch("checklist", selected.id, { status });
      upsertLocal("checklist", row as unknown as { id?: string });
      setSelected(row as unknown as ChecklistItem);
    } catch (e) {
      window.alert((e as Error).message);
    }
  }

  return (
    <div className="space-y-6" data-testid="ln-checklist">
      <Card testId="ln-cl-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search checklist items…" testId="ln-cl-search" />
        <div className="mt-4">
          <TabBar<ChecklistItem["status"] | "all">
            testId="ln-cl-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",          label: "All"         },
              { key: "todo",         label: "Todo"        },
              { key: "in-progress",  label: "In progress" },
              { key: "blocked",      label: "Blocked"     },
              { key: "done",         label: "Done"        },
            ]}
          />
        </div>
      </Card>

      {/* ── Stage lanes ─────────────────────────────────────────── */}
      {STAGES.map((stage) => {
        const items = byStage.get(stage) || [];
        if (items.length === 0) return null;
        const doneCount = items.filter((i) => i.status === "done").length;
        const pct = Math.round((doneCount / Math.max(1, items.length)) * 100);
        return (
          <Card key={stage} padded={false} testId={`ln-stage-${stage.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="p-5 flex items-baseline justify-between" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <SectionHeader eyebrow={`Stage · ${stage}`} title={`${items.length} item${items.length === 1 ? "" : "s"}`} />
              <div className="text-sm tabular-nums" style={{ color: pct === 100 ? "var(--ph-success)" : pct >= 50 ? "var(--ph-warning)" : "var(--ph-ink-muted)" }}>
                {doneCount}/{items.length} · {pct}%
              </div>
            </div>
            <div className="p-2">
              {items.map((c) => {
                const m = STATUS_META[c.status];
                return (
                  <ListRow key={c.id} testId={`ln-c-${c.id}`}
                    onClick={() => { setSelected(c); setWsTab("overview"); }}
                    label={<span className="inline-flex items-center gap-2">{c.title}
                      <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{m.label}</span>
                    </span>}
                    sublabel={<>{launchName(c.launchId)} · {c.category} · {c.owner} · {c.dueRelative}</>}
                  />
                );
              })}
            </div>
          </Card>
        );
      })}

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Checklist Item 360" testId="ln-c-drawer" width={720}>
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: STATUS_META[selected.status].fg }}>{STATUS_META[selected.status].label} · {stageForItem(selected)}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{selected.title}</div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<ChkTab>
                testId="ln-c-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",    label: "Overview"    },
                  { value: "owner",       label: "Owner"       },
                  { value: "due",         label: "Due"         },
                  { value: "approval",    label: "Approval"    },
                  { value: "blocked",     label: "Blockers"    },
                  { value: "comments",    label: "Comments"    },
                  { value: "attachments", label: "Attachments" },
                  { value: "timeline",    label: "Timeline"    },
                  { value: "audit",       label: "Audit"       },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="ln-c-ws-overview">
                <Field label="Launch"    inline><span className="text-sm">{launchName(selected.launchId)}</span></Field>
                <Field label="Category"  inline><span className="text-sm">{selected.category}</span></Field>
                <Field label="Stage"     inline><span className="text-sm font-semibold">{stageForItem(selected)}</span></Field>
                <Field label="Owner"     inline><span className="text-sm">{selected.owner}</span></Field>
                <Field label="Due"       inline><span className="text-sm">{selected.dueRelative}</span></Field>
                <Field label="Status"    inline><span className="text-sm">{STATUS_META[selected.status].label}</span></Field>
              </div>
            )}

            {wsTab === "owner" && <div data-testid="ln-c-ws-owner"><Field label="Owner" inline><span className="text-sm">{selected.owner}</span></Field></div>}
            {wsTab === "due" && <div data-testid="ln-c-ws-due"><Field label="Due" inline><span className="text-sm">{selected.dueRelative}</span></Field></div>}

            {wsTab === "approval" && (
              <div data-testid="ln-c-ws-approval">
                <Field label="Requires approval" inline><span className="text-sm">{selected.category === "Regulatory" || selected.category === "Formula" ? "Yes · Executive" : "No"}</span></Field>
                <div className="text-[10px] uppercase tracking-[0.14em] mt-3 mb-1" style={{ color: "var(--ph-ink-muted)" }}>Chain</div>
                <div className="text-sm">Requester → Manager → Finance → Executive → Owner</div>
              </div>
            )}

            {wsTab === "blocked" && (
              <div data-testid="ln-c-ws-blocked">
                <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                  {selected.status === "blocked" ? "Blocked pending an upstream dependency. Escalate to the owner or file a decision request." : "Not currently blocked."}
                </div>
              </div>
            )}

            {wsTab === "comments" && <div data-testid="ln-c-ws-comments"><div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Discussion threads open at audit level.</div></div>}
            {wsTab === "attachments" && <div data-testid="ln-c-ws-attachments"><div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Attach documents at the linked task/decision level.</div></div>}

            {wsTab === "timeline" && (
              <div data-testid="ln-c-ws-timeline">
                <Field label="Due"    inline><span className="text-sm">{selected.dueRelative}</span></Field>
                <Field label="Status" inline><span className="text-sm">{STATUS_META[selected.status].label}</span></Field>
                <AuditTrail collection="checklist" recordId={selected.id} testId="ln-c-timeline-audit" />
              </div>
            )}

            {wsTab === "audit" && <div data-testid="ln-c-ws-audit"><AuditTrail collection="checklist" recordId={selected.id} testId="ln-c-audit" /></div>}

            <div className="pt-4 flex flex-wrap justify-end gap-2">
              {selected.status !== "done"    && <Button testId="ln-c-mark-done"    onClick={() => transition("done")}>Mark done</Button>}
              {selected.status !== "blocked" && <Button variant="secondary" testId="ln-c-block" onClick={() => transition("blocked")}>Flag blocked</Button>}
              {selected.status !== "in-progress" && <Button variant="secondary" testId="ln-c-start" onClick={() => transition("in-progress")}>Start</Button>}
              {selected.status !== "todo" && <Button variant="secondary" testId="ln-c-reset" onClick={() => transition("todo")}>Reset</Button>}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
